/**
 * Per-study site membership helpers.
 *
 * {@code user.assignments} is the source of truth: [{ protocolNumber, siteIds }].
 * Empty siteIds on a row means all sites in that study. Documents that only
 * have the legacy flat studyIds/siteIds lists are synthesized here.
 */

export function effectiveAssignments(user) {
  if (!user) return [];
  if (Array.isArray(user.assignments) && user.assignments.length > 0) {
    return normalize(user.assignments);
  }
  return fromLegacyLists(user.studyIds || [], user.siteIds || []);
}

export function hasUnrestrictedAccess(user) {
  const roles = user?.customData?.roles || user?.roles || [];
  const privileged = roles.includes('admin') || roles.includes('support');
  return privileged && effectiveAssignments(user).length === 0;
}

export function hasAssignedStudies(user) {
  return effectiveAssignments(user).length > 0;
}

export function hasAnyAssignedSites(user) {
  return effectiveAssignments(user).some((row) => row.siteIds.length > 0);
}

export function assignmentForProtocol(user, protocolNumber) {
  if (!protocolNumber) return null;
  return effectiveAssignments(user).find((row) => row.protocolNumber === protocolNumber) || null;
}

/** True when the current study is site-scoped (specific sites, not all). */
export function currentAssignmentHasSites(user, protocolNumber) {
  const row = assignmentForProtocol(user, protocolNumber);
  return !!(row && row.siteIds.length > 0);
}

export function accessSummary(user) {
  const roles = user?.customData?.roles || user?.roles || [];
  const assignments = effectiveAssignments(user);
  if (!assignments.length) {
    if (roles.includes('admin') || roles.includes('support')) {
      return { text: 'All studies', title: 'All studies' };
    }
    return { text: '—', title: '' };
  }
  const parts = assignments.map((row) =>
    row.siteIds.length
      ? `${row.protocolNumber} (${row.siteIds.join(', ')})`
      : `${row.protocolNumber} (all sites)`
  );
  const title = parts.join(', ');
  const text = parts.length <= 2 ? title : `${parts.slice(0, 2).join(', ')} +${parts.length - 2}`;
  return { text, title };
}

export function toSearchParam(user) {
  const assignments = effectiveAssignments(user);
  const tokens = [];
  for (const row of assignments) {
    if (!row.siteIds.length) {
      tokens.push(row.protocolNumber);
    } else {
      for (const siteId of row.siteIds) {
        tokens.push(`${row.protocolNumber}:${siteId}`);
      }
    }
  }
  return tokens.join(',');
}

export function normalize(list) {
  if (!Array.isArray(list) || list.length === 0) return [];
  const byProtocol = new Map();
  for (const row of list) {
    if (!row) continue;
    const protocol = String(row.protocolNumber || '').trim();
    if (!protocol) continue;
    const sites = cleanStrings(row.siteIds);
    if (!byProtocol.has(protocol)) {
      byProtocol.set(protocol, { protocolNumber: protocol, siteIds: [], allSites: false });
    }
    const merged = byProtocol.get(protocol);
    if (sites.length === 0) {
      merged.allSites = true;
      merged.siteIds = [];
    } else if (!merged.allSites) {
      for (const siteId of sites) {
        if (!merged.siteIds.includes(siteId)) merged.siteIds.push(siteId);
      }
    }
  }
  return [...byProtocol.values()].map(({ protocolNumber, siteIds, allSites }) => ({
    protocolNumber,
    siteIds: allSites ? [] : siteIds
  }));
}

function fromLegacyLists(studyIds, siteIds) {
  const protocols = cleanStrings(studyIds);
  const sites = cleanStrings(siteIds);
  if (!protocols.length) return [];
  return protocols.map((protocolNumber) => ({
    protocolNumber,
    siteIds: [...sites]
  }));
}

function cleanStrings(values) {
  if (!Array.isArray(values)) return [];
  const out = [];
  for (const value of values) {
    if (value == null) continue;
    const trimmed = String(value).trim();
    if (!trimmed || out.includes(trimmed)) continue;
    out.push(trimmed);
  }
  return out;
}
